import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import SearchRounded from '@mui/icons-material/SearchRounded';
import type { SelectChangeEvent } from '@mui/material';
import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { useCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import type {
  OpenCommentFilter,
  SentimentFilter,
  Sorting,
  TopicFilter,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import { styles } from './index.styles';

export const OPEN_COMMENTS_FILTER_TEST_ID = 'open-comments-filter';
export const OPEN_COMMENTS_FILTER_TEXT_FIELD_TEST_ID =
  'open-comments-filter-text-field';
export const OPEN_COMMENTS_TEXT_FILTER_SEARCH_ICON_TEST_ID =
  'open-comments-text-filter-search-icon';
export const OPEN_COMMENTS_TEXT_FILTER_LOADING_ICON_TEST_ID =
  'open-comments-text-filter-loading-icon';

export interface OpenCommentsFilterProps {
  setOpenCommentFilter: Dispatch<SetStateAction<OpenCommentFilter>>;
  openCommentFilter: OpenCommentFilter;
  withoutRelevance?: boolean;
}

const OpenCommentsFilter: FC<OpenCommentsFilterProps> = ({
  setOpenCommentFilter,
  openCommentFilter,
  withoutRelevance,
}) => {
  const { t } = useTranslation('dashboard');
  const { textFilter, setTextFilter, textFilterLoading } = useCodingResults();

  const handleTextFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTextFilter(event.target.value);
  };

  const menuProps = {
    PaperProps: {
      sx: styles.menuProps,
    },
  };

  const topicFilterData: { label: string; value: TopicFilter }[] = [
    { label: t('OpenComments.overview'), value: 'all' },
    { label: t('OpenComments.topics'), value: 'topics' },
    { label: t('OpenComments.subtopics'), value: 'subtopics' },
  ];

  const sentimentFilterData: { label: string; value: SentimentFilter }[] = [
    { label: t('OpenComments.allSentiments'), value: 'totalOfAllCounts' },
    { label: t('OpenComments.positiveTopics'), value: 'positiveCount' },
    { label: t('OpenComments.neutralTopics'), value: 'neutralCount' },
    { label: t('OpenComments.negativeTopics'), value: 'negativeCount' },
  ];

  const sortingData: { label: string; value: Sorting }[] = [
    { label: t('OpenComments.sortFrequency'), value: 'frequency' },
    ...(withoutRelevance
      ? []
      : [
          {
            label: t('OpenComments.sortRelevance'),
            value: 'relevance' as Sorting,
          },
        ]),
  ];
  const selectComponents = [
    {
      value: openCommentFilter.sentimentFilter,
      onChange: (event: SelectChangeEvent) => {
        setOpenCommentFilter((prev) => ({
          ...prev,
          sentimentFilter: event.target.value as SentimentFilter,
        }));
      },
      options: sentimentFilterData,
      name: 'openCommentFilter.sentimentFilter',
    },
    {
      value: openCommentFilter.topicFilter,
      onChange: (event: SelectChangeEvent) => {
        setOpenCommentFilter((prev) => ({
          ...prev,
          topicFilter: event.target.value as TopicFilter,
        }));
      },
      options: topicFilterData,
      name: 'openCommentFilter.topicFilter',
    },
    {
      value: openCommentFilter.sorting,
      onChange: (event: SelectChangeEvent) => {
        setOpenCommentFilter((prev) => ({
          ...prev,
          sorting: event.target.value as Sorting,
        }));
      },
      options: sortingData,
      name: 'openCommentFilter.sorting',
    },
  ];

  return (
    <Stack sx={styles.stack} data-test-id={OPEN_COMMENTS_FILTER_TEST_ID}>
      <TextField
        data-test-id={OPEN_COMMENTS_FILTER_TEXT_FIELD_TEST_ID}
        sx={styles.textField}
        placeholder={t('common:search')}
        onChange={handleTextFilterChange}
        value={textFilter}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {textFilterLoading ? (
                <CircularProgress
                  size="1.5rem"
                  data-test-id={OPEN_COMMENTS_TEXT_FILTER_LOADING_ICON_TEST_ID}
                />
              ) : (
                <SearchRounded
                  data-test-id={OPEN_COMMENTS_TEXT_FILTER_SEARCH_ICON_TEST_ID}
                />
              )}
            </InputAdornment>
          ),
        }}
      />
      {selectComponents.map(({ value, onChange, options, name }, index) => (
        <Select
          key={index}
          sx={styles.select}
          value={value}
          onChange={onChange}
          MenuProps={menuProps}
          role="button"
          data-test-id={name}
        >
          {options.map(({ label, value }) => (
            <MenuItem value={value} key={value} sx={styles.menuItem}>
              {label}
            </MenuItem>
          ))}
        </Select>
      ))}
    </Stack>
  );
};

export default OpenCommentsFilter;
